import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu } from "lucide-react";
import { useCart } from "@/context/CartContext";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/vins", label: "Nos vins" },
  { to: "/reservation", label: "Dégustation" },
  { to: "/contact", label: "Contact" },
];

const Logo = ({ className = "" }) => (
  <img
    src="/logo-casavino.png"
    alt="Casa Vino — Vino artisanale di Puglia"
    className={`block object-contain ${className}`}
    draggable={false}
  />
);

const Header = () => {
  const { count } = useCart();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F6F0]/90 border-b border-[#E8E2D2]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="brand-logo-link"
          aria-label="Casa Vino — accueil"
          className="flex items-center shrink-0"
        >
          <Logo className="h-12 md:h-14 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] lg:text-xs tracking-[0.18em] uppercase font-medium transition-colors ${
                  isActive
                    ? "text-[#5B2333]"
                    : "text-[#1A1617] hover:text-[#5B2333]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-3">
          <Link
            to="/panier"
            data-testid="cart-link"
            className="relative p-2 rounded-full hover:bg-[#E8E2D2]/40 transition-colors"
            aria-label="Panier"
          >
            <ShoppingBag className="w-5 h-5 text-[#1A1617]" strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-[#5B2333] rounded-full"
              >
                {count}
              </span>
            )}
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Menu"
                className="md:hidden p-2 rounded-full hover:bg-[#E8E2D2]/40"
              >
                <Menu className="w-5 h-5 text-[#1A1617]" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#F9F6F0] border-l border-[#E8E2D2] w-[82vw] sm:w-[400px]"
              data-testid="mobile-menu-content"
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex justify-center mb-10 mt-4">
                <Logo className="h-24 w-auto" />
              </div>
              <nav className="flex flex-col gap-5">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `font-heading text-2xl ${isActive ? "text-[#5B2333]" : "text-[#1A1617]"}`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
